// The one place a reconciliation record status is turned into a colour and a description.
//
// There were three copies of this switch — TaskReconciliationPage, TaskConfigurationPage and
// MessageLogPage — and they had already drifted (only two of them knew about DLQ), so a record read as
// dead-lettered on one screen and plain grey on another. Adding a status meant remembering all three.
//
// The utility class names (bg-*/text-*) are hand-rolled per screen, not Tailwind, so every stylesheet
// that renders a status badge must define the pairs used here. See the WARNING block in
// TaskReconciliationPage.css / SyncPage.css / SyncProjectDetailsPage.css.

// WARNING is terminal and successful: the write reached the target, but the target rejected part of it
// (e.g. a preference whose type/value is missing from the target's Data Value Mappings — OHIP returns
// 201 and still writes the profile). It needs a person to look at it, NOT the pipeline to run it again,
// which is exactly why it is not ERROR. Amber sits between the red of a failure and the green of a
// clean record. Kept in step with TERMINAL_RECORD_STATUSES in the util-layer's opensearch.js.
const STATUS_STYLES = {
    'IN-FLIGHT': 'bg-purple-100 text-purple-800',
    PENDING:     'bg-yellow-100 text-yellow-800',
    REQUESTED:   'bg-blue-100 text-blue-800',
    PROCESSING:  'bg-cyan-100 text-cyan-800',
    // Queued by an async target (OHIP stay records answer 202) — sent, outcome not yet known.
    SUBMITTED:   'bg-cyan-100 text-cyan-800',
    ERROR:       'bg-red-100 text-red-800',
    // Darker/bolder red so a dead-lettered record stands out from a (retrying) ERROR.
    DLQ:         'bg-red-200 text-red-900',
    WARNING:     'bg-amber-100 text-amber-800',
    SKIPPED:     'bg-gray-100 text-gray-800',
    PROCESSED:   'bg-green-100 text-green-800',
    COMPLETE:    'bg-green-100 text-green-800',
};

const DEFAULT_STYLE = 'bg-gray-100 text-gray-800';

/** Badge classes for a record status. Unknown/absent statuses fall back to neutral grey. */
export const getRecordStatusColor = (status) => STATUS_STYLES[String(status || '').toUpperCase()] || DEFAULT_STYLE;

// Hover text for a status badge. Only the ones whose meaning is not obvious from the word carry one —
// a tooltip on "PROCESSED" is noise, a tooltip on "WARNING" is the whole point.
const STATUS_TITLES = {
    WARNING: 'Synced to the target, but the target rejected part of the record. No reprocess needed — someone has to fix the underlying configuration.',
    DLQ: 'Dead-lettered: the record failed repeatedly and was moved off the queue.',
    'IN-FLIGHT': 'Not in a finished state — the pipeline still owes this record work.',
    SUBMITTED: 'Sent to OHIP and queued for processing (202). The outcome is not known yet — run Verify Stay Records to get it.',
    REQUESTED: 'Asked for through OXI by SyncConnect and not back yet. It becomes PROCESSED when OPERA answers and the record has been tokenised and completed.',
};

export const getRecordStatusTitle = (status) => STATUS_TITLES[String(status || '').toUpperCase()] || undefined;

/** True when the record reached the target successfully (WARNING included — the write went through). */
export const isSuccessfulRecordStatus = (status) =>
    ['PROCESSED', 'SUCCESS', 'VAULTED', 'SKIPPED', 'WARNING'].includes(String(status || '').toUpperCase());

/**
 * The Record Status filter options, in the order they are offered.
 *
 * Shared for the same reason the colours are: TaskReconciliationPage and TaskConfigurationPage each had
 * their own hand-written <option> list, and they drifted — WARNING was added to one and not the other, so
 * a task summary could report a warning that the filter beneath it had no way to select. A count you can
 * see and cannot search for is worse than no count.
 *
 * IN-FLIGHT is a DERIVED state ("not in a terminal status"), which is what the summary counts — not a
 * literal value anything writes. QUEUED / PROCESSING / COMPLETE were deliberately removed: nothing writes
 * them to the SPR store, so selecting them always returned nothing. REQUESTED is back: SyncConnect writes it
 * for PEP-RESERVATIONS (oxi-to-pep) while a reservation is out with OXI.
 *
 * Keep in step with TERMINAL_RECORD_STATUSES in the util-layer's opensearch.js.
 */
export const RECORD_STATUS_OPTIONS = [
    { value: '',          label: 'All Statuses' },
    { value: 'IN-FLIGHT', label: 'In-Flight (not finished)' },
    { value: 'PENDING',   label: 'Pending' },
    { value: 'SUBMITTED', label: 'Submitted (awaiting OHIP outcome)' },
    { value: 'REQUESTED', label: 'Requested (awaiting OXI)' },
    { value: 'ERROR',     label: 'Error' },
    { value: 'WARNING',   label: 'Warning (synced, partly rejected)' },
    { value: 'DLQ',       label: 'Dead-lettered (DLQ)' },
    { value: 'SKIPPED',   label: 'Skipped' },
    { value: 'PROCESSED', label: 'Processed' },
];
